/**
 * C07 Mascot — 陪伴角色狀態機 (學習端專用)
 *
 * 對齊 docs/GAME_TECHNIQUES_WIKI.md C07 (Khan Kids Kodi 移植):
 * 學習端答題反饋出現的陪伴 mascot — 答對誇獎 / 答錯鼓勵 / 連對 streak 慶祝。
 *
 * 重點紅線(來自 CLAUDE.md 1.1 / memory feedback_research_extract_game_dev_value):
 *   - 學習與遊戲完全分離 → mascot **只**屬於學習端,絕對不在戰鬥 scene 出現。
 *     spec 文字裡有「mascot 也出現在戰鬥場景 HUD」一句,以 CLAUDE.md 為準,不實作。
 *   - 對話 9 歲適齡 — 不過於幼稚 / 不過於成熟。
 *
 * 設計:
 *   - 純邏輯模組,不碰 DOM。UI 由 mascot-ui.ts 負責,讓測試能直接驗證狀態機。
 *   - 對話池從 mascot-lines.json import,靜態載入(Vite/Vitest 都支援 JSON import)。
 *   - 連對門檻 3 / 5 / 10 對齊 quiz/engine.ts 的 combo multiplier 階梯。
 *   - 隨機抽詞支援注入 rng,讓測試決定性。
 */
import linesData from './mascot-lines.json';

/** Mascot 內部視覺狀態(UI 對應換 emoji / 動畫) */
export type MascotState =
  | 'idle'              // 進入畫面、答題之間的休息態
  | 'cheer'             // 答對(一般)
  | 'encourage'         // 答錯
  | 'streak-celebrate'; // 連對里程碑(3/5/10)

/** 連對里程碑 — 對應 lines bucket 的等級 */
export type StreakTier = 0 | 3 | 5 | 10;

/** UI 反饋資料 — 餵給 mascot-ui.ts 顯示一輪 */
export interface MascotFeedback {
  state: MascotState;
  line: string;
  /** 觸發時的 combo;0 = 重置 / 非 streak */
  combo: number;
  /** 達到的 streak tier(0 / 3 / 5 / 10);非 streak-celebrate 一律 0 */
  tier: StreakTier;
}

interface MascotLines {
  cheer: string[];
  encourage: string[];
  streak3: string[];
  streak5: string[];
  streak10: string[];
  idle: string[];
}

const LINES = linesData as unknown as MascotLines;

/** 連對里程碑門檻;按順序檢查,先取最高達成的 tier */
const STREAK_TIERS: readonly StreakTier[] = [10, 5, 3] as const;

function pickLine(pool: string[], rng: () => number): string {
  if (pool.length === 0) return '';
  const i = Math.floor(rng() * pool.length);
  // Math.random 偶爾回 1 — 取 min 保險
  return pool[Math.min(i, pool.length - 1)];
}

function poolForTier(tier: StreakTier): string[] {
  switch (tier) {
    case 10: return LINES.streak10;
    case 5:  return LINES.streak5;
    case 3:  return LINES.streak3;
    default: return LINES.cheer;
  }
}

function tierForCombo(combo: number): StreakTier {
  for (const t of STREAK_TIERS) {
    if (combo >= t) return t;
  }
  return 0;
}

/**
 * 純函式 reducer:給 (combo, correct) 算出該顯示的 mascot 反饋。
 *   - correct=true, combo 達 3/5/10 → streak-celebrate(對應 tier)
 *   - correct=true,其他 → cheer
 *   - correct=false → encourage(combo 重置由 QuizEngine 負責,這裡不維護)
 *
 * combo 來自 QuizEngine.recordAnswer 回傳的 combo 值,呼叫端傳入即可。
 */
export function computeMascotFeedback(
  combo: number,
  correct: boolean,
  rng: () => number = Math.random,
): MascotFeedback {
  if (!correct) {
    return {
      state: 'encourage',
      line: pickLine(LINES.encourage, rng),
      combo: 0,
      tier: 0,
    };
  }
  const tier = tierForCombo(combo);
  if (tier > 0) {
    return {
      state: 'streak-celebrate',
      line: pickLine(poolForTier(tier), rng),
      combo,
      tier,
    };
  }
  return {
    state: 'cheer',
    line: pickLine(LINES.cheer, rng),
    combo,
    tier: 0,
  };
}

/** 給 UI 進場使用的 idle 文案 */
export function idleLine(rng: () => number = Math.random): string {
  return pickLine(LINES.idle, rng);
}

/**
 * Mascot 控制器 — 封裝狀態 + 計時(讓 UI 可以「秀 1.5s 後回 idle」)。
 * UI 層 (mascot-ui.ts) subscribe 後跟著畫;測試可直接拿這隻當 SUT。
 */
export class MascotController {
  private state: MascotState = 'idle';
  private listeners = new Set<(fb: MascotFeedback) => void>();
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private rng: () => number;

  /** Cheer/encourage 顯示時間(ms);streak 顯示得久一點,看得到慶祝 */
  static readonly CHEER_DURATION_MS = 1200;
  static readonly STREAK_DURATION_MS = 1800;

  constructor(rng: () => number = Math.random) {
    this.rng = rng;
  }

  /** 讀目前狀態 */
  getState(): MascotState { return this.state; }

  /** Subscribe — UI 來掛 */
  subscribe(fn: (fb: MascotFeedback) => void): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  /**
   * 對外主入口:答題後呼叫,自動產出反饋並通知 listeners,
   * 然後在 cheer/streak 顯示結束後自動回 idle。
   */
  react(combo: number, correct: boolean): MascotFeedback {
    const fb = computeMascotFeedback(combo, correct, this.rng);
    this.state = fb.state;
    this.emit(fb);
    if (this.idleTimer) clearTimeout(this.idleTimer);
    const duration = fb.state === 'streak-celebrate'
      ? MascotController.STREAK_DURATION_MS
      : MascotController.CHEER_DURATION_MS;
    this.idleTimer = setTimeout(() => this.toIdle(), duration);
    return fb;
  }

  /** 主動切回 idle(escape 離開答題 / lesson 結束時用) */
  toIdle(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    this.state = 'idle';
    const fb: MascotFeedback = {
      state: 'idle',
      line: idleLine(this.rng),
      combo: 0,
      tier: 0,
    };
    this.emit(fb);
  }

  /** Clean up — UI 卸載時呼叫 */
  destroy(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    this.listeners.clear();
  }

  private emit(fb: MascotFeedback): void {
    for (const fn of this.listeners) fn(fb);
  }
}

/** 暴露對話池讓測試讀(避免測試 hard-code 字串) */
export const __TEST_LINES__ = LINES;
